// src/api/sseManager.ts
// handler for streaming output progress manager (mainview API)

let currentES: EventSource | null = null;

/* “单例”： 全局只保留“一个”实例。每次打开新连接前先释放旧连接。
把 EventSource 想成一根“电话线”。
单例 = 家里只装 一根 电话线。要打新电话（新请求）时，先挂断旧电话，再拨新号。
这样就不会家里到处都是没挂断的电话，占线路、占资源，还让别人打不进来（浏览器的并发连接上限）。

在代码里怎么做到“只保留一个”？
- 用一个模块级的变量 currentES 来保存当前这根电话线。
- 每次要新建 EventSource 时，先检查 currentES：
  - 有旧线？→ currentES.close() 先挂断。
  - 再把新建的 es 赋值给 currentES，表示“现在家里这根电话线换成新的了”。
- 出错或结束时，再把它关掉并清空，避免“忘记挂断”。

为什么要这么做？
- 浏览器同源连接数有限：旧的长连接不关，新连接就可能卡在 CONNECTING。
- 资源可控：不会积累一堆“僵尸连接”。
- 用户体验好：第二次、第三次请求都能立刻连上，不再久等“Connecting…”。
*/


/** 打开（或切换）SSE 连接；返回一个 close 函数用于安全关闭 */
export function openProgressStream(
  url: string,
  handlers: {
    onOpen?: () => void;
    onMessage?: (payload: any) => void;
    onError?: (err?: any) => void;
  }
) {
  // 先关掉上一条还在占坑的连接，避免“第二次调用 Connecting 卡很久”
  if (currentES) {
    try { currentES.close(); } catch {}
    currentES = null;
  }

  const es = new EventSource(url);
  currentES = es;  // ⭐️把当前实例保存为“单例”

  es.onopen = () => {
    handlers.onOpen?.();
  };

  es.onmessage = (ev) => {
    try {
      const payload = JSON.parse(ev.data);
      handlers.onMessage?.(payload);
    } catch {
      // 忽略非 JSON 消息（如注释/心跳）
    }
  };

  es.onerror = (e) => {
    handlers.onError?.(e);
    try { es.close(); } catch {}
    if (currentES === es) currentES = null;  // ⭐️错误时也释放单例
  };

  return () => {
    try { es.close(); } catch {}
    if (currentES === es) currentES = null;  // ⭐️确保释放
  };
}

/** 主动关闭（可在组件卸载时调用） */
export function closeProgressStream() {
  if (currentES) {
    try { currentES.close(); } catch {}
    currentES = null;
  }
}
